// src/pages/BrowseRoles.js

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Form, Spinner, Row, Col, Modal } from 'react-bootstrap';
import { Search, X, FilterX, Filter } from 'lucide-react';
import { fetchPublicRoleListApi } from '../services/api';
import ModernAuditionCard from '../components/ModernAuditionCard';
import '../components/css/BrowseRoles.css'; 

const BrowseRoles = () => {
  const [allRoles, setAllRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // Filter States
  // const [selectedAuditionType, setSelectedAuditionType] = useState('All Types');
  // const [selectedCity, setSelectedCity] = useState('All Cities');
  // const [selectedTalentType, setSelectedTalentType] = useState('All Talent');
  // const [selectedProjectType, setSelectedProjectType] = useState('All Projects');
  // const [selectedRoleType, setSelectedRoleType] = useState('All Role Types');
  // const [selectedRoleCategory, setSelectedRoleCategory] = useState('All Categories');
  // const [selectedAuditionLocation, setSelectedAuditionLocation] = useState('All Locations');
  // const [selectedGender, setSelectedGender] = useState('All Genders');

  const [selectedAuditionType, setSelectedAuditionType] = useState([]);
  const [selectedCity, setSelectedCity] = useState([]);
  const [selectedTalentType, setSelectedTalentType] = useState([]);
  const [selectedProjectType, setSelectedProjectType] = useState([]);
  const [selectedRoleType, setSelectedRoleType] = useState([]);
  const [selectedRoleCategory, setSelectedRoleCategory] = useState([]);
  const [selectedAuditionLocation, setSelectedAuditionLocation] = useState([]);
  const [selectedGender, setSelectedGender] = useState([]);
  // Mobile Popup State
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    const loadAllRoles = async () => {
      try {
        setLoading(true);
        const { data } = await fetchPublicRoleListApi();
        setAllRoles(data?.data || []);
      } catch (err) {
        console.error('Error loading roles:', err);
        setAllRoles([]);
      } finally { setLoading(false); }
    };
    loadAllRoles();
  }, []);

  const handleCheckboxChange = (
  value,
  selectedValues,
  setSelectedValues
) => {
  if (selectedValues.includes(value)) {
    setSelectedValues(
      selectedValues.filter((item) => item !== value)
    );
  } else {
    setSelectedValues([...selectedValues, value]);
  }
};

  // ... (all useMemo hooks remain the same) ...
  const auditionTypes = useMemo(() => {
    const set = new Set();
    allRoles.forEach((item) => {
      const type = item.auditionType || item.audition_type || '';
      if (type && String(type).trim()) set.add(String(type).trim());
    });
    return ['All Types', ...Array.from(set)];
  }, [allRoles]);

  const cities = useMemo(() => {
    const set = new Set();
    allRoles.forEach((item) => {
      const city = item.city || item.projectCity || item.location || item.roleCity || item['project.city'] || '';
      if (city && String(city).trim()) set.add(String(city).trim());
    });
    return ['All Cities', ...Array.from(set)];
  }, [allRoles]);

  const talentTypes = useMemo(() => {
    const set = new Set();
    allRoles.forEach((item) => {
      const type = item.roleCategory || item.role_category || item.category || item.talentType || '';
      if (type && String(type).trim()) set.add(String(type).trim());
    });
    return ['All Talent', ...Array.from(set)];
  }, [allRoles]);

    const projectTypes = useMemo(() => {
    const set = new Set();

    allRoles.forEach((item) => {
      const value =
        item.projectType ||
        item.project_type ||
        '';

      if (value && String(value).trim()) {
        set.add(String(value).trim());
      }
    });

    return ['All Projects', ...Array.from(set)];
  }, [allRoles]);

  const roleTypes = useMemo(() => {
    const set = new Set();

    allRoles.forEach((item) => {
      const value =
        item.roleType ||
        item.role_type ||
        '';

      if (value && String(value).trim()) {
        set.add(String(value).trim());
      }
    });

    return ['All Role Types', ...Array.from(set)];
  }, [allRoles]);

  const roleCategories = useMemo(() => {
    const set = new Set();

    allRoles.forEach((item) => {
      const value =
        item.roleCategory ||
        item.role_category ||
        '';

      if (value && String(value).trim()) {
        set.add(String(value).trim());
      }
    });

    return ['All Categories', ...Array.from(set)];
  }, [allRoles]);

  const auditionLocations = useMemo(() => {
    const set = new Set();

    allRoles.forEach((item) => {
      const value =
        item.auditionLocation ||
        item.audition_location ||
        '';

      if (value && String(value).trim()) {
        set.add(String(value).trim());
      }
    });

    return ['All Locations', ...Array.from(set)];
  }, [allRoles]);

  const genders = useMemo(() => {
    const set = new Set();

    allRoles.forEach((item) => {
      const value =
        item.gender ||
        item.requiredGender ||
        '';

      if (value && String(value).trim()) {
        set.add(String(value).trim());
      }
    });

    return ['All Genders', ...Array.from(set)];
  }, [allRoles]);

  const filteredRoles = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return allRoles.filter((item) => {
      const roleName = (item.roleName || item.role_name || item.roleTitle || '').toLowerCase();
      const projectName = (item.projectName || item.project_name || item['project.projectName'] || '').toLowerCase();
      const city = (item.city || item.projectCity || item.location || item.roleCity || item['project.city'] || '').toLowerCase();
      const talentType = (item.roleCategory || item.role_category || item.category || item.talentType || '').toLowerCase();
      const auditionType = (item.auditionType || item.audition_type || '').toLowerCase();
      const projectType = (item.projectType || item.project_type || '').toLowerCase();
      const roleType = (item.roleType || item.role_type || '').toLowerCase();
      const roleCategory = (item.roleCategory || item.role_category || '').toLowerCase();
      const auditionLocation = (item.auditionLocation || item.audition_location || '').toLowerCase();
      const gender = (item.gender || item.requiredGender || '').toLowerCase();

      const matchesSearch = !term || roleName.includes(term) || projectName.includes(term);
      // const matchesAuditionType = selectedAuditionType === 'All Types' || auditionType === selectedAuditionType.toLowerCase() || auditionType.includes(selectedAuditionType.toLowerCase());
      // const matchesCity = selectedCity === 'All Cities' || city === selectedCity.toLowerCase() || city.includes(selectedCity.toLowerCase());
      // const matchesTalentType = selectedTalentType === 'All Talent' || talentType === selectedTalentType.toLowerCase() || talentType.includes(selectedTalentType.toLowerCase());
      
      
      // const matchesProjectType =
      //   selectedProjectType === 'All Projects' ||
      //   projectType.includes(selectedProjectType.toLowerCase());

      // const matchesRoleType =
      //   selectedRoleType === 'All Role Types' ||
      //   roleType.includes(selectedRoleType.toLowerCase());

      // const matchesRoleCategory =
      //   selectedRoleCategory === 'All Categories' ||
      //   roleCategory.includes(selectedRoleCategory.toLowerCase());

      // const matchesAuditionLocation =
      //   selectedAuditionLocation === 'All Locations' ||
      //   auditionLocation.includes(selectedAuditionLocation.toLowerCase());

      // const matchesGender =
      //   selectedGender === 'All Genders' ||
      //   gender.includes(selectedGender.toLowerCase());

      const matchesAuditionType =
  selectedAuditionType.length === 0 ||
  selectedAuditionType.some((item) =>
    auditionType.includes(item.toLowerCase())
  );

const matchesCity =
  selectedCity.length === 0 ||
  selectedCity.some((item) =>
    city.includes(item.toLowerCase())
  );

const matchesTalentType =
  selectedTalentType.length === 0 ||
  selectedTalentType.some((item) =>
    talentType.includes(item.toLowerCase())
  );

const matchesProjectType =
  selectedProjectType.length === 0 ||
  selectedProjectType.some((item) =>
    projectType.includes(item.toLowerCase())
  );

const matchesRoleType =
  selectedRoleType.length === 0 ||
  selectedRoleType.some((item) =>
    roleType.includes(item.toLowerCase())
  );

const matchesRoleCategory =
  selectedRoleCategory.length === 0 ||
  selectedRoleCategory.some((item) =>
    roleCategory.includes(item.toLowerCase())
  );

const matchesAuditionLocation =
  selectedAuditionLocation.length === 0 ||
  selectedAuditionLocation.some((item) =>
    auditionLocation.includes(item.toLowerCase())
  );

const matchesGender =
  selectedGender.length === 0 ||
  selectedGender.some((item) =>
    gender.includes(item.toLowerCase())
  );
      return matchesSearch && matchesAuditionType && matchesCity && matchesTalentType && matchesProjectType && matchesRoleType && matchesRoleCategory && matchesAuditionLocation && matchesGender;
    });
  }, [allRoles, searchTerm, selectedAuditionType, selectedCity, selectedTalentType,selectedProjectType,selectedRoleType,selectedRoleCategory,selectedAuditionLocation,selectedGender]);

  const clearAllFilters = () => {
    setSearchTerm('');
    // setSelectedAuditionType('All Types');
    // setSelectedCity('All Cities');
    // setSelectedTalentType('All Talent');
    // setSelectedProjectType('All Projects');
    // setSelectedRoleType('All Role Types');
    // setSelectedRoleCategory('All Categories');
    // setSelectedAuditionLocation('All Locations');
    // setSelectedGender('All Genders');
    setSelectedAuditionType([]);
    setSelectedCity([]);
    setSelectedTalentType([]);
    setSelectedProjectType([]);
    setSelectedRoleType([]);
    setSelectedRoleCategory([]);
    setSelectedAuditionLocation([]);
    setSelectedGender([]);
  };

  // const hasActiveFilters = selectedAuditionType !== 'All Types' || selectedCity !== 'All Cities' || selectedTalentType !== 'All Talent' || selectedProjectType !== 'All Projects' ||   selectedRoleType !== 'All Role Types' ||   selectedRoleCategory !== 'All Categories' ||   selectedAuditionLocation !== 'All Locations' ||  selectedGender !== 'All Genders' ;
  const hasActiveFilters =
  selectedAuditionType.length > 0 ||
  selectedCity.length > 0 ||
  selectedTalentType.length > 0 ||
  selectedProjectType.length > 0 ||
  selectedRoleType.length > 0 ||
  selectedRoleCategory.length > 0 ||
  selectedAuditionLocation.length > 0 ||
  selectedGender.length > 0;

  return (
    <div className="browse-roles-page">
      <section className="browse-roles-hero">
        <Container fluid className="px-3 px-md-4 px-xl-5">
          
          <div className="browse-roles-header-wrapper">
            <div className="browse-roles-title-wrap">
              <h1 className="browse-roles-title">Browse <span>Roles</span></h1>
              <p className="browse-roles-subtitle">Find your next big opportunity from our verified listings.</p>
            </div>
          </div>

          {/* <div className="top-filter-bar">
            <button className="mobile-filter-icon-btn d-lg-none" onClick={() => setShowMobileFilters(true)}>
              <Filter size={20} />
              {hasActiveFilters && <span className="mobile-filter-badge"></span>}
            </button>
            <div className="filter-dropdowns d-none d-lg-flex">
              <Form.Select className="dark-filter-select" value={selectedAuditionType} onChange={(e) => setSelectedAuditionType(e.target.value)}>
                {auditionTypes.map((type) => (<option key={type} value={type}>{type.replace(/_/g, ' ')}</option>))}
              </Form.Select>
              <Form.Select className="dark-filter-select" value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)}>
                {cities.map((city) => (<option key={city} value={city}>{city}</option>))}
              </Form.Select>
              {talentTypes.length > 1 && (
                <Form.Select className="dark-filter-select" value={selectedTalentType} onChange={(e) => setSelectedTalentType(e.target.value)}>
                  {talentTypes.map((type) => (<option key={type} value={type}>{type}</option>))}
                </Form.Select>
              )}
              {hasActiveFilters && (
                <button onClick={clearAllFilters} className="clear-filters-btn" title="Clear Filters"><FilterX size={18} /></button>
              )}
            </div>
            <div className="browse-roles-search">
              <Search size={18} className="browse-search-icon" />
              <Form.Control
                type="text"
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="browse-search-input"
              />
              {searchTerm && (
                <button type="button" className="browse-clear-btn" onClick={() => setSearchTerm('')}><X size={16} /></button>
              )}
            </div>
          </div> */}

          <Row className="mt-3">
  
            {/* LEFT FILTER SIDEBAR */}
            {/* <Col lg={3} xl={2} className="d-none d-lg-block"> */}
            <Col lg={4} xl={3} xxl={2} className="d-none d-lg-block">
              <div className="browse-sidebar-filters">

                <h5 className="sidebar-filter-title">Filters</h5>

                <div className="mb-3">
                  <label className="drawer-filter-label">AUDITION TYPE</label>
                  {/* <Form.Select
                    className="dark-filter-select w-100"
                    value={selectedAuditionType}
                    onChange={(e) => setSelectedAuditionType(e.target.value)}
                  >
                    {auditionTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </Form.Select> */}
                  <div className="filter-checkbox-group">
                    {auditionTypes.map((type) => (
                      <Form.Check
                        key={type}
                        type="checkbox"
                        label={type}
                        checked={selectedAuditionType.includes(type)}
                        onChange={() =>
                          handleCheckboxChange(
                            type,
                            selectedAuditionType,
                            setSelectedAuditionType
                          )
                        }
                        className="custom-dark-checkbox"
                      />
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="drawer-filter-label">CITY</label>
                  {/* <Form.Select
                    className="dark-filter-select w-100"
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                  >
                    {cities.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </Form.Select> */}

                  <div className="filter-checkbox-group">
                    {cities.map((city) => (
                      <Form.Check
                        key={city}
                        type="checkbox"
                        id={`city-${city}`}
                        label={city}
                        checked={selectedCity.includes(city)}
                        onChange={() =>
                          handleCheckboxChange(
                            city,
                            selectedCity,
                            setSelectedCity
                          )
                        }
                        className="custom-dark-checkbox"
                      />
                    ))}
                  </div>
                </div>

                {/* {talentTypes.length > 1 && (
                  <div className="mb-3">
                    <label className="drawer-filter-label">TALENT TYPE</label>
                    <Form.Select
                      className="dark-filter-select w-100"
                      value={selectedTalentType}
                      onChange={(e) => setSelectedTalentType(e.target.value)}
                    >
                      {talentTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                )} */}

                {talentTypes.length > 0 && (
                    <div className="mb-3">
                      <label className="drawer-filter-label">TALENT TYPE</label>

                      <div className="filter-checkbox-group">
                        {talentTypes.map((type) => (
                          <Form.Check
                            key={type}
                            type="checkbox"
                            label={type}
                            checked={selectedTalentType.includes(type)}
                            onChange={() =>
                              handleCheckboxChange(
                                type,
                                selectedTalentType,
                                setSelectedTalentType
                              )
                            }
                            className="custom-dark-checkbox"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                {/* PROJECT TYPE */}
              {/* <div className="mb-3">
                <label className="drawer-filter-label">PROJECT TYPE</label>

                <Form.Select
                  className="dark-filter-select w-100"
                  value={selectedProjectType}
                  onChange={(e) => setSelectedProjectType(e.target.value)}
                >
                  {projectTypes.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </div> */}
              <div className="mb-3">
                <label className="drawer-filter-label">PROJECT TYPE</label>

                <div className="filter-checkbox-group">
                  {projectTypes.map((item) => (
                    <Form.Check
                      key={item}
                      type="checkbox"
                      label={item}
                      checked={selectedProjectType.includes(item)}
                      onChange={() =>
                        handleCheckboxChange(
                          item,
                          selectedProjectType,
                          setSelectedProjectType
                        )
                      }
                      className="custom-dark-checkbox"
                    />
                  ))}
                </div>
              </div>

              {/* ROLE TYPE */}
              <div className="mb-3">
                <label className="drawer-filter-label">ROLE TYPE</label>

                <div className="filter-checkbox-group">
                  {roleTypes.map((item) => (
                    <Form.Check
                      key={item}
                      type="checkbox"
                      label={item}
                      checked={selectedRoleType.includes(item)}
                      onChange={() =>
                        handleCheckboxChange(
                          item,
                          selectedRoleType,
                          setSelectedRoleType
                        )
                      }
                      className="custom-dark-checkbox"
                    />
                  ))}
                </div>
              </div>
              {/* ROLE CATEGORY */}
              <div className="mb-3">
              <label className="drawer-filter-label">ROLE CATEGORY</label>

              <div className="filter-checkbox-group">
                {roleCategories.map((item) => (
                  <Form.Check
                    key={item}
                    type="checkbox"
                    label={item}
                    checked={selectedRoleCategory.includes(item)}
                    onChange={() =>
                      handleCheckboxChange(
                        item,
                        selectedRoleCategory,
                        setSelectedRoleCategory
                      )
                    }
                    className="custom-dark-checkbox"
                  />
                ))}
              </div>
            </div>
              {/* AUDITION LOCATION */}
              {/* <div className="mb-3">
                <label className="drawer-filter-label">AUDITION LOCATION</label>

                <Form.Select
                  className="dark-filter-select w-100"
                  value={selectedAuditionLocation}
                  onChange={(e) => setSelectedAuditionLocation(e.target.value)}
                >
                  {auditionLocations.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Form.Select>
              </div> */}
              <div className="mb-3">
              <label className="drawer-filter-label">AUDITION LOCATION</label>

              <div className="filter-checkbox-group">
                {auditionLocations.map((item) => (
                  <Form.Check
                    key={item}
                    type="checkbox"
                    label={item}
                    checked={selectedAuditionLocation.includes(item)}
                    onChange={() =>
                      handleCheckboxChange(
                        item,
                        selectedAuditionLocation,
                        setSelectedAuditionLocation
                      )
                    }
                    className="custom-dark-checkbox"
                  />
                ))}
              </div>
            </div>

              {/* GENDER */}
              <div className="mb-3">
                <label className="drawer-filter-label">GENDER</label>

                <div className="filter-checkbox-group">
                  {genders.map((item) => (
                    <Form.Check
                      key={item}
                      type="checkbox"
                      label={item}
                      checked={selectedGender.includes(item)}
                      onChange={() =>
                        handleCheckboxChange(
                          item,
                          selectedGender,
                          setSelectedGender
                        )
                      }
                      className="custom-dark-checkbox"
                    />
                  ))}
                </div>
              </div>

                {hasActiveFilters && (
                  <button
                    onClick={clearAllFilters}
                    className="btn-clear-filters w-100 mt-2"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </Col>

            {/* RIGHT CONTENT */}
            <Col lg={9} xl={10}>
            {/* <Col lg={8} xl={9} xxl={10}> */}

              {/* SEARCH + MOBILE FILTER */}
              <div className="top-filter-bar mb-4">

                <button
                  className="mobile-filter-icon-btn d-lg-none"
                  onClick={() => setShowMobileFilters(true)}
                >
                  <Filter size={20} />
                  {hasActiveFilters && (
                    <span className="mobile-filter-badge"></span>
                  )}
                </button>

                <div className="browse-roles-search">
                  <Search size={18} className="browse-search-icon" />

                  <Form.Control
                    type="text"
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="browse-search-input"
                  />

                  {searchTerm && (
                    <button
                      type="button"
                      className="browse-clear-btn"
                      onClick={() => setSearchTerm('')}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* CARDS */}
              <div className="browse-roles-content w-100">

                {loading ? (
                  <div className="browse-empty-box">
                    <Spinner animation="border" variant="danger" />
                  </div>
                ) : filteredRoles.length > 0 ? (

                  <Row className="g-3 w-100 m-0">
                    {filteredRoles.map((item, index) => (
                      <Col
                        xxl={3}
                        xl={4}
                        lg={4}
                        md={6}
                        xs={6}
                        key={item.id || item.localRoleId || index}
                      >
                        <ModernAuditionCard audition={item} />
                      </Col>
                    ))}
                  </Row>

                ) : (
                  <div className="browse-empty-box">
                    <div className="browse-empty-icon">
                      <Search size={56} strokeWidth={1.8} />
                    </div>

                    <h3>No roles found</h3>

                    <p>Try adjusting your filters or search terms.</p>

                    <button
                      type="button"
                      className="browse-clear-all"
                      onClick={clearAllFilters}
                    >
                      Clear all filters
                    </button>
                  </div>
                )}

              </div>

            </Col>
          </Row>
            
        </Container>
      </section>

      {/* MODAL (remains the same) */}
      <Modal 
        show={showMobileFilters} 
        onHide={() => setShowMobileFilters(false)} 
        centered 
        dialogClassName="compact-dark-modal"
      >
        {/* <Modal.Header closeButton closeVariant="white" className="modal-custom-header border-bottom border-dark">
          <Modal.Title className="fw-bold d-flex align-items-center gap-2 text-white" style={{fontSize: '14px', letterSpacing: '1px'}}>
            <Filter size={18} className="text-danger" /> FILTER ROLES
          </Modal.Title>
        </Modal.Header> */}

    <Modal.Header
  closeButton
  closeVariant="white"
  className="modal-custom-header border-bottom border-dark"
>

  <div className="mobile-filter-header-wrap">

    <Modal.Title className="mobile-filter-title">
      <Filter size={18} className="text-danger" />
      FILTER ROLES
    </Modal.Title>

    <div className="mobile-filter-header-actions">

      {/* APPLY */}

      <button
        className="mobile-filter-header-btn apply-header-btn"
        onClick={() => setShowMobileFilters(false)}
      >
        Apply Filters
      </button>

      {/* CLEAR */}

      {hasActiveFilters && (
        <button
          className="mobile-filter-header-btn clear-header-btn"
          onClick={() => {
            clearAllFilters();
            setShowMobileFilters(false);
          }}
        >
          Clear All
        </button>
      )}

    </div>

  </div>

</Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3 p-4">
      {/* AUDITION TYPE */}
      <div className="filter-group-popup">
        <label className="drawer-filter-label">AUDITION TYPE</label>

        <div className="filter-checkbox-group">
          {auditionTypes.map((item) => (
            <Form.Check
              key={item}
              type="checkbox"
              label={item}
              checked={selectedAuditionType.includes(item)}
              onChange={() =>
                handleCheckboxChange(
                  item,
                  selectedAuditionType,
                  setSelectedAuditionType
                )
              }
              className="custom-dark-checkbox"
            />
          ))}
        </div>
      </div>

      {/* CITY */}
      <div className="filter-group-popup">
        <label className="drawer-filter-label">CITY</label>

        <div className="filter-checkbox-group">
          {cities.map((item) => (
            <Form.Check
              key={item}
              type="checkbox"
              label={item}
              checked={selectedCity.includes(item)}
              onChange={() =>
                handleCheckboxChange(
                  item,
                  selectedCity,
                  setSelectedCity
                )
              }
              className="custom-dark-checkbox"
            />
          ))}
        </div>
      </div>

      {/* TALENT TYPE */}
      <div className="filter-group-popup">
        <label className="drawer-filter-label">TALENT TYPE</label>

        <div className="filter-checkbox-group">
          {talentTypes.map((item) => (
            <Form.Check
              key={item}
              type="checkbox"
              label={item}
              checked={selectedTalentType.includes(item)}
              onChange={() =>
                handleCheckboxChange(
                  item,
                  selectedTalentType,
                  setSelectedTalentType
                )
              }
              className="custom-dark-checkbox"
            />
          ))}
        </div>
      </div>

      {/* PROJECT TYPE */}
      <div className="filter-group-popup">
        <label className="drawer-filter-label">PROJECT TYPE</label>

        <div className="filter-checkbox-group">
          {projectTypes.map((item) => (
            <Form.Check
              key={item}
              type="checkbox"
              label={item}
              checked={selectedProjectType.includes(item)}
              onChange={() =>
                handleCheckboxChange(
                  item,
                  selectedProjectType,
                  setSelectedProjectType
                )
              }
              className="custom-dark-checkbox"
            />
          ))}
        </div>
      </div>

      {/* ROLE TYPE */}
      <div className="filter-group-popup">
        <label className="drawer-filter-label">ROLE TYPE</label>

        <div className="filter-checkbox-group">
          {roleTypes.map((item) => (
            <Form.Check
              key={item}
              type="checkbox"
              label={item}
              checked={selectedRoleType.includes(item)}
              onChange={() =>
                handleCheckboxChange(
                  item,
                  selectedRoleType,
                  setSelectedRoleType
                )
              }
              className="custom-dark-checkbox"
            />
          ))}
        </div>
      </div>

      {/* ROLE CATEGORY */}
      <div className="filter-group-popup">
        <label className="drawer-filter-label">ROLE CATEGORY</label>

        <div className="filter-checkbox-group">
          {roleCategories.map((item) => (
            <Form.Check
              key={item}
              type="checkbox"
              label={item}
              checked={selectedRoleCategory.includes(item)}
              onChange={() =>
                handleCheckboxChange(
                  item,
                  selectedRoleCategory,
                  setSelectedRoleCategory
                )
              }
              className="custom-dark-checkbox"
            />
          ))}
        </div>
      </div>

      {/* AUDITION LOCATION */}
      <div className="filter-group-popup">
        <label className="drawer-filter-label">AUDITION LOCATION</label>

        <div className="filter-checkbox-group">
          {auditionLocations.map((item) => (
            <Form.Check
              key={item}
              type="checkbox"
              label={item}
              checked={selectedAuditionLocation.includes(item)}
              onChange={() =>
                handleCheckboxChange(
                  item,
                  selectedAuditionLocation,
                  setSelectedAuditionLocation
                )
              }
              className="custom-dark-checkbox"
            />
          ))}
        </div>
      </div>

      {/* GENDER */}
      <div className="filter-group-popup">
        <label className="drawer-filter-label">GENDER</label>

        <div className="filter-checkbox-group">
          {genders.map((item) => (
            <Form.Check
              key={item}
              type="checkbox"
              label={item}
              checked={selectedGender.includes(item)}
              onChange={() =>
                handleCheckboxChange(
                  item,
                  selectedGender,
                  setSelectedGender
                )
              }
              className="custom-dark-checkbox"
            />
          ))}
        </div>
      </div>


{/* </Modal.Body> */}
          {/* <div className="mt-3">
             <button className="btn-apply-filters w-100 mb-2" onClick={() => setShowMobileFilters(false)}>Apply Filters</button>
             {hasActiveFilters && (
                <button className="btn-clear-filters w-100" onClick={() => { clearAllFilters(); setShowMobileFilters(false); }}>Clear All</button>
             )}
          </div> */}
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default BrowseRoles;